import { useMemo, useState } from "react";
import BaseTable from "../common/BaseTable";
import TabContainer from "../common/TabContainer";
import { useMarkdownRenderer } from "../hooks/useMarkdownRenderer";
import { useAppDesignData } from "../hooks/useTableData";
import type { AppDesignParsedContent, AppDesignTableProps } from "../types";
import styles from "./index.module.scss";

type TableKey = "appTable" | "connectorTable" | "functionTable";

const TABLE_CONFIG: { key: TableKey; label: string; labelMap: Record<string, string> }[] = [
  {
    key: "appTable",
    label: "作成するアプリ",
    labelMap: {
      app_id: "アプリID",
      app_name: "アプリ名",
      description: "説明",
    },
  },
  {
    key: "connectorTable",
    label: "アプリ間の連携",
    labelMap: {
      connector_id: "連携ID",
      sid: "データ元アプリID",
      tid: "連携先アプリID",
      relationship: "連携方法",
      keyfield: "キーフィールド",
      referencefield: "参照フィールド",
    },
  },
  {
    key: "functionTable",
    label: "アプリ毎の機能",
    labelMap: {
      app_id: "アプリID",
      rid: "機能ID",
      name: "機能",
      description: "説明",
      how: "実装方法",
    },
  },
];

const getTableData = (parsedContent: AppDesignParsedContent | null, tabIndex: number) => {
  if (!parsedContent) return { headers: [] as string[], rows: [] as string[][] };
  const config = TABLE_CONFIG[tabIndex];
  if (!config) return { headers: [] as string[], rows: [] as string[][] };
  const table = parsedContent[config.key];
  if (!table?.headers || !table?.rows) return { headers: [] as string[], rows: [] as string[][] };
  return { headers: table.headers, rows: table.rows };
};

const AppDesignTable: React.FC<AppDesignTableProps> = ({
  formData,
  content,
  mode,
  maxRows = 3,
  className = "",
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const {
    parsed: parsedContent,
    isMarkdown,
    isUnknownJson,
    content: rawContent,
  } = useAppDesignData(formData, content);
  const { renderMarkdown } = useMarkdownRenderer();

  // useMemoをトップレベルに配置（Reactフックのルールに従う）
  const tabs = useMemo(() => {
    if (!parsedContent) return [];
    return TABLE_CONFIG.map((config, index) => {
      const tableData = getTableData(parsedContent, index);
      return {
        key: config.key,
        label: config.label,
        content: (
          <BaseTable
            mode={mode}
            headers={tableData.headers}
            rows={tableData.rows}
            labelMap={config.labelMap}
            maxRows={mode === "compact" ? maxRows : undefined}
          />
        ),
      };
    });
  }, [parsedContent, mode, maxRows]);

  // JSONだが期待する形式でない場合は整形して表示
  if (isUnknownJson) {
    try {
      const formatted = JSON.stringify(JSON.parse(rawContent), null, 2);
      return (
        <div
          className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
        >
          <pre className={styles["jsonPreview"]}>
            {mode === "compact" ? `${formatted.slice(0, 300)}...` : formatted}
          </pre>
        </div>
      );
    } catch {
      return <div className={styles["empty"]}>データがありません</div>;
    }
  }

  if (isMarkdown) {
    // 空のコンテンツの場合は「データがありません」を表示
    if (!rawContent || rawContent.trim() === "") {
      return <div className={styles["empty"]}>データがありません</div>;
    }
    // compactモードでは重いMarkdownレンダリングを避けてテキスト表示
    if (mode === "compact") {
      const truncated = rawContent.substring(0, 300);
      return (
        <div className={`${styles["wrapper"]} ${styles["wrapper--compact"]} ${className}`}>
          <div className={styles["markdownContent--compact"]}>
            <pre className={styles["markdownPreviewText"]}>{truncated}...</pre>
          </div>
        </div>
      );
    }
    return (
      <div className={`${styles["wrapper"]} ${className}`}>
        <div className={styles["markdownContent"]}>{renderMarkdown(rawContent)}</div>
      </div>
    );
  }

  if (!parsedContent) {
    return <div className={styles["empty"]}>データがありません</div>;
  }

  return (
    <div
      className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
    >
      <TabContainer
        mode={mode}
        tabs={tabs}
        selectedIndex={selectedTab}
        onSelect={setSelectedTab}
        maxTabs={mode === "compact" ? 3 : undefined}
      />
    </div>
  );
};

export default AppDesignTable;
